import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Search, X, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { CATEGORIES, catLabel, catLines } from "../lib/api";

const SearchOverlay = ({ onClose }) => {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    nav(`/search?q=${encodeURIComponent(q.trim())}`);
    onClose();
  };
  return (
    <motion.div
      className="fixed inset-0 z-[70] bg-hot text-ink flex flex-col px-5 sm:px-10 py-6"
      initial={{ clipPath: "inset(0 0 100% 0)" }}
      animate={{ clipPath: "inset(0 0 0% 0)" }}
      exit={{ clipPath: "inset(0 0 100% 0)" }}
      transition={{ duration: 0.7, ease: [0.7, 0, 0.2, 1] }}
      data-testid="search-overlay"
    >
      <div className="flex justify-between items-center">
        <span className="kicker">Search the archive — 100 stories</span>
        <button onClick={onClose} className="p-2 hover:rotate-90 transition-transform" data-testid="search-close-btn" aria-label="Close search">
          <X className="h-7 w-7" />
        </button>
      </div>
      <form onSubmit={submit} className="my-auto">
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Type & hit enter"
          className="w-full bg-transparent border-b-2 border-ink display text-5xl sm:text-8xl placeholder:text-ink/40 outline-none pb-4"
          data-testid="search-overlay-input"
        />
        <div className="mt-6 flex flex-wrap gap-2">
          {["skate", "nails", "rave", "painting", "vintage", "skincare"].map((t) => (
            <button key={t} type="button" onClick={() => setQ(t)} className="kicker border border-ink px-3 py-1.5 rounded-full hover:bg-ink hover:text-hot transition-colors" data-testid={`search-suggestion-${t}`}>
              {t}
            </button>
          ))}
        </div>
      </form>
    </motion.div>
  );
};

const MobileMenu = ({ onClose }) => (
  <motion.div
    className="flip fixed inset-0 z-[65] bg-ink text-cream px-5 pt-24 pb-10 flex flex-col"
    initial={{ y: "-100%" }}
    animate={{ y: 0 }}
    exit={{ y: "-100%" }}
    transition={{ duration: 0.6, ease: [0.7, 0, 0.2, 1] }}
    data-testid="mobile-menu"
  >
    {CATEGORIES.map((c, i) => (
      <motion.div key={c} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 + i * 0.07 }}>
        <Link to={`/category/${c}`} onClick={onClose} className={`display ${c === "art" ? "text-6xl" : "text-7xl"} block py-1 hover:opacity-60 transition-opacity`} data-testid={`mobile-nav-${c}`}>
          {catLines(c).map((l) => <span key={l} className="block">{l}</span>)}
        </Link>
      </motion.div>
    ))}
    <Link to="/from-the-web" onClick={onClose} className="display text-5xl block py-1 mt-2 underline decoration-4 underline-offset-8" data-testid="mobile-nav-from-the-web">
      From the web
    </Link>
    <p className="mt-auto kicker text-cream/70">Issue 27 · Autumn 2026</p>
  </motion.div>
);

export const Header = () => {
  const [search, setSearch] = useState(false);
  const [menu, setMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-[66] transition-[background-color,border-color,padding] duration-500 border-b ${
          scrolled || menu ? "bg-cream/85 backdrop-blur-xl border-ink/10 py-3" : "bg-cream/60 backdrop-blur-md border-transparent py-4"
        }`}
        data-testid="site-header"
      >
        <div className="max-w-[1440px] mx-auto px-5 sm:px-10 flex items-center justify-between">
          <Link to="/" className="group" data-testid="header-logo-link" onClick={() => setMenu(false)}>
            <Logo light={false} />
          </Link>
          <nav className="hidden md:flex items-center gap-8">
            {CATEGORIES.map((c) => (
              <NavLink
                key={c}
                to={`/category/${c}`}
                className={({ isActive }) => `kicker link-underline ${isActive ? "text-rose" : "text-ink"} hover:text-rose transition-colors`}
                data-testid={`nav-${c}`}
              >
                {catLabel(c)}
              </NavLink>
            ))}
            <NavLink
              to="/from-the-web"
              className={({ isActive }) => `kicker link-underline ${isActive ? "text-rose" : "text-ink"} hover:text-rose transition-colors`}
              data-testid="nav-from-the-web"
            >
              From the web
            </NavLink>
          </nav>
          <div className="flex items-center gap-2">
            <button onClick={() => setSearch(true)} className="h-10 w-10 grid place-items-center rounded-full border border-ink/20 hover:bg-ink hover:border-ink hover:text-cream transition-colors" data-testid="header-search-btn" aria-label="Search">
              <Search className="h-4 w-4" />
            </button>
            <Link to="/category/fashion" className="hidden lg:flex items-center gap-1 kicker bg-ink text-cream rounded-full px-4 py-2.5 border border-ink hover:bg-hot hover:text-ink transition-colors" data-testid="header-latest-issue-btn">
              Issue 27 <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
            <button onClick={() => setMenu((m) => !m)} className="md:hidden h-10 w-10 grid place-items-center rounded-full bg-ink text-cream" data-testid="mobile-menu-toggle" aria-label="Menu">
              {menu ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>{search && <SearchOverlay onClose={() => setSearch(false)} />}</AnimatePresence>
      <AnimatePresence>{menu && <MobileMenu onClose={() => setMenu(false)} />}</AnimatePresence>
    </>
  );
};
